import { useFetcher } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { maskCNPJ, maskPhone, validateCNPJ, validateName, validatePhone } from '../helpers/utilsValidation'
import InlineAlert from '../primitives/InlineAlert'
import PrivacyConsentGroup from './PrivacyConsentGroup'
import { Button, Loading, TextField } from '~/design-system/primitives'
import { useGlobalToast } from '~/hooks/useGlobalToast'
import { Save } from '~/icons'
import type { ProfilePj } from '~/type/User'

interface FormLegalPersonalDataProps {
  profile: ProfilePj
  setEditPersonalData: (value: boolean) => void
  fetcherGetPersonalData: () => void
}

interface ResponsePersonalData {
  response: ProfilePj
  success: boolean
}

export default function FormLegalPersonalData({
  profile,
  setEditPersonalData,
  fetcherGetPersonalData,
}: FormLegalPersonalDataProps) {
  const fetcher = useFetcher()
  const { show } = useGlobalToast()
  const [isLoading, setIsLoading] = useState(false)

  const [formValues, setFormValues] = useState<{
    [key: string]: string | boolean
  }>({
    firstName: '',
    lastName: '',
    email: '',
    document: '',
    phone: '',
    corporateName: '',
    userProfileId: '',
    politica_privacidade: false,
    swift_vc: false,
    swiftEvoceComunicacao: false,
  })

  const [errors, setErrors] = useState<{ [key: string]: string | null }>({})

  const handleInputChange = (name: string, value: string, validate?: (value: string) => string | null) => {
    setFormValues((prev) => ({ ...prev, [name]: value }))
    const error = validate?.(value) ?? null
    setErrors((prev) => ({ ...prev, [name]: error }))
  }

  const getErrorMessage = (name: keyof typeof formValues) => {
    const value = formValues[name]
    const isEmpty = typeof value === 'string' && value.trim() === ''
    return errors[name] ?? (isEmpty ? 'Preencha este campo.' : '')
  }

  const handleCNPJChange = (value: string) => {
    const masked = maskCNPJ(value)
    handleInputChange('document', masked, validateCNPJ)
  }

  const handlePhoneChange = (value: string) => {
    const masked = maskPhone(value)
    handleInputChange('phone', masked, validatePhone)
  }

  const handleOtpSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault()

    const formData = new FormData()
    formData.append('firstName', formValues.firstName as string)
    formData.append('lastName', formValues.lastName as string)
    formData.append('isCorporate', formValues.isCorporate as string)
    formData.append('corporateName', formValues.corporateName as string)
    formData.append('stateRegistration', formValues.stateRegistration as string)
    formData.append('corporateDocument', formValues.corporateDocument as string)
    formData.append('tradeName', formValues.tradeName as string)
    formData.append('email', formValues.email as string)
    formData.append('country', formValues.country as string)
    formData.append('state', formValues.state as string)
    formData.append('city', formValues.city as string)
    formData.append('phone', formValues.phone as string)
    formData.append('document', formValues.document as string)
    formData.append('documentType', formValues.documentType as string)
    formData.append('actionType', 'updateUserPj')
    formData.append('swift_vc', formValues.swift_vc ? 'true' : 'false')
    formData.append('politica_privacidade', formValues.politica_privacidade ? 'true' : 'false')
    formData.append('swiftEvoceComunicacao', formValues.swiftEvoceComunicacao ? 'true' : 'false')

    fetcher.submit(formData, { method: 'post', action: '/myaccount' })
    setIsLoading(true)
  }

  const isFormValid = () => {
    return (
      validateName(formValues.firstName as string) === null &&
      validateName(formValues.lastName as string) === null &&
      validateName(formValues.corporateName as string) === null &&
      validateCNPJ(formValues.corporateDocument as string) === null &&
      validatePhone(formValues.phone as string) === null &&
      formValues.politica_privacidade === true
    )
  }

  useEffect(() => {
    if (profile) {
      const parsedConsents = Object.fromEntries(
        profile.consents.map((c: { key: string; value: boolean }) => [c.key, c.value]),
      )
      const isPrivacyPolicyAccepted = parsedConsents.politica_privacidade === true

      setFormValues({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        isCorporate: profile.isCorporate || '',
        corporateName: profile.corporateName || '',
        stateRegistration: profile.stateRegistration || '',
        corporateDocument: maskCNPJ(profile.corporateDocument) || '',
        tradeName: profile.tradeName || '',
        email: profile.email || '',
        country: profile.country || '',
        state: profile.state || '',
        city: profile.city || '',
        phone: maskPhone(profile.phone || ''),
        document: profile.document || '',
        politica_privacidade: isPrivacyPolicyAccepted,
        swift_vc: isPrivacyPolicyAccepted ? parsedConsents.swift_vc || false : true,
        swiftEvoceComunicacao: isPrivacyPolicyAccepted ? parsedConsents.swiftEvoceComunicacao || false : true,
        documentType: profile.documentType.toLowerCase() === 'cpf' ? 'CNPJ' : 'CNPJ',
      })
    }
  }, [profile])

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data) {
      const data = fetcher.data as ResponsePersonalData

      if (data?.success) {
        show({ message: 'Dados alterados com sucesso', type: 'success' })
      } else {
        show({ message: 'Não foi possível alterar os dados', type: 'error' })
      }
      setTimeout(() => {
        setIsLoading(false)
        setEditPersonalData(false)
        fetcherGetPersonalData()
      }, 1000)
    }
  }, [fetcher])

  const consentValues = [
    ...(formValues.politica_privacidade ? ['politica_privacidade'] : []),
    ...(formValues.swift_vc ? ['swift_vc'] : []),
    ...(formValues.swiftEvoceComunicacao ? ['swiftEvoceComunicacao'] : []),
  ]

  const handleConsentChange = (updatedKeys: string[]) => {
    setFormValues((prev) => ({
      ...prev,
      politica_privacidade: updatedKeys.includes('politica_privacidade'),
      swift_vc: updatedKeys.includes('swift_vc'),
      swiftEvoceComunicacao: updatedKeys.includes('swiftEvoceComunicacao'),
    }))
  }

  return (
    <>
      {!isFormValid() && (
        <InlineAlert
          message="Preencha todos os campos obrigatórios e aceite a política de privacidade para salvar seus dados."
          title="Campos obrigatórios"
          variant="negative"
        />
      )}
      <fetcher.Form className="flex flex-col gap-4" onSubmit={handleOtpSubmit}>
        <div className="flex flex-col lg:flex-row gap-4">
          <TextField
            className="lg:w-1/3"
            id="email"
            isDisabled={true}
            label="Email:"
            name="email"
            type="email"
            value={String(formValues.email)}
          />
          <TextField
            className="lg:w-1/3"
            label="Empresa"
            name="corporateName"
            type="string"
            value={String(formValues.corporateName)}
            isRequired
            onChange={(value) => handleInputChange('corporateName', value)}
          />
          <TextField
            className="lg:w-1/3"
            errorMessage={getErrorMessage('corporateDocument')}
            label="CNPJ *"
            maxLength={14}
            name="corporateDocument"
            type="tel"
            validate={validateCNPJ}
            value={String(formValues.corporateDocument)}
            isRequired
            onChange={handleCNPJChange}
          />
        </div>
        <div className="flex flex-col lg:flex-row gap-4">
          <TextField
            className="lg:w-1/3"
            label="Primeiro nome *"
            name="firstName"
            type="text"
            value={String(formValues.firstName)}
            isRequired
            onChange={(value) => handleInputChange('firstName', value)}
          />
          <TextField
            className="lg:w-1/3"
            label="Último Nome *"
            name="lastName"
            type="text"
            value={String(formValues.lastName)}
            isRequired
            onChange={(value) => handleInputChange('lastName', value)}
          />
          <TextField
            className="lg:w-1/3"
            errorMessage={getErrorMessage('phone')}
            label="Telefone celular *"
            maxLength={14}
            name="phone"
            type="tel"
            validate={validatePhone}
            value={String(formValues.phone)}
            isRequired
            onChange={handlePhoneChange}
          />
        </div>
        <PrivacyConsentGroup isCorporate={profile.isCorporate} disableSwift_vc isSubmitting={isLoading} value={consentValues} onChange={handleConsentChange} />
        <div className="flex lg:justify-end gap-2">
          <Button
            className="flex w-full lg:w-fit justify-center"
            color="primary"
            isDisabled={!isFormValid()}
            type="submit"
          >
            <Save className="fill-white mr-1 text-xl" />
            Salvar
          </Button>
        </div>
      </fetcher.Form>
      {isLoading && <Loading />}
    </>
  )
}
